import { faceCopy, faceMoveAlongNormal, faceNew, type Face } from "geom/brep";
import type { FaceFaceIntersection } from "geom/intersect";
import { intersectFaceFace } from "geom/intersect";
import { planeIsCoplanar, planeIsParallel, planeReProjectPolyLine2 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonIntersection } from "geom/polyclip/polyClip";

type GeometryValue = { kind: "face"; value: Face } | { kind: "plane"; value: Plane };

function getFace(geom: GeometryValue): Face | null {
  return geom.kind === "face" ? geom.value : null;
}

function getPlane(geom: GeometryValue): Plane | null {
  switch (geom.kind) {
    case "face":
      return geom.value.plane;
    case "plane":
      return geom.value;
  }
}

/**
 * A geometry primitive with geometric comparison methods.
 */
export interface GeometryQuery {
  readonly geom?: GeometryValue;
  readonly parallelWith: (other: GeometryQuery) => boolean;
  readonly coplanarWith: (other: GeometryQuery, distTolerance?: number) => boolean;
  readonly planarOverlapWith: (other: GeometryQuery) => Face[];
  readonly intersectsWith: (other: GeometryQuery, otherOffset?: number) => FaceFaceIntersection | null;
}

export const emptyGeometryQuery: GeometryQuery = {
  parallelWith: () => false,
  coplanarWith: () => false,
  planarOverlapWith: () => [],
  intersectsWith: () => null,
};

export function faceGeometryQueryNew(face: Face): GeometryQuery {
  return makeGeometryQuery({ kind: "face", value: face });
}

export function planeGeometryQueryNew(plane: Plane): GeometryQuery {
  return makeGeometryQuery({ kind: "plane", value: plane });
}

function makeGeometryQuery(geom: GeometryValue): GeometryQuery {
  return {
    geom,

    parallelWith(other: GeometryQuery): boolean {
      const b = other.geom;
      if (!b) {
        return false;
      }
      const planeA = getPlane(geom);
      const planeB = getPlane(b);
      if (!planeA || !planeB) {
        return false;
      }
      return planeIsParallel(planeA, planeB);
    },

    coplanarWith(other: GeometryQuery, distTolerance?: number): boolean {
      const b = other.geom;
      if (!b) {
        return false;
      }
      const planeA = getPlane(geom);
      const planeB = getPlane(b);
      if (!planeA || !planeB) {
        return false;
      }
      return planeIsCoplanar(planeA, planeB, distTolerance);
    },

    planarOverlapWith(other: GeometryQuery): Face[] {
      const b = other.geom;
      if (!b) {
        return [];
      }
      const faceA = getFace(geom);
      const faceB = getFace(b);
      if (!faceA || !faceB) {
        return [];
      }

      const reprojected = faceNew(
        faceB.loops.map((loop) => planeReProjectPolyLine2(loop, faceB.plane, faceA.plane)),
        faceA.plane,
      );

      return polygonIntersection({ pls: [faceA.loops, reprojected.loops] }).map((loops) => faceNew(loops, faceA.plane));
    },

    intersectsWith(other: GeometryQuery, otherOffset: number = 0): FaceFaceIntersection | null {
      const b = other.geom;
      if (!b) {
        return null;
      }
      const faceA = getFace(geom);
      const faceB = getFace(b);
      if (!faceA || !faceB) {
        return null;
      }

      if (otherOffset !== 0) {
        const faceB2 = faceCopy(faceB);
        faceMoveAlongNormal(faceB2, otherOffset);
        return intersectFaceFace(faceA, faceB2, []);
      }

      return intersectFaceFace(faceA, faceB, []);
    },
  };
}
